import { deleteSettlementAccount, setAccountAsDefault } from '@basqet-dashboard/common/api';
import { ReactComponent as Delete } from '@basqet-dashboard/common/assets/icons/delete.svg';
import { ReactComponent as Ellipsis } from '@basqet-dashboard/common/assets/icons/ellipsis.svg';
import { ReactComponent as Home } from '@basqet-dashboard/common/assets/icons/home.svg';
import { ReactComponent as Settings } from '@basqet-dashboard/common/assets/icons/settings.svg';
import { Button, SuccessModal, WarningModal } from '@basqet-dashboard/common/components';
import { formatErrorMessage, formatWallet } from '@basqet-dashboard/common/utils';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { toast } from 'react-toastify';

const Account = ({ settlement, currencies, onAccountChange }) => {
  const [currentModal, setCurrentModal] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const formatCurrency = (id) => {
    let currency = currencies?.find(({ value }) => value === id);
    return currency && currency.label;
  };
  const setAsDefault = async () => {
    const { account_address: address, account_type: type } = settlement;
    setIsLoading(true);
    try {
      await setAccountAsDefault({ address, type });
      setIsLoading(false);
      setCurrentModal('success');
      onAccountChange();
    } catch (error) {
      formatErrorMessage(error);
      setIsLoading(false);
    }
  };
  const deleteAccount = async () => {
    const { id } = settlement;
    setIsLoading(true);
    try {
      await deleteSettlementAccount(id);
      setIsLoading(false);
      toast.success('Account Deleted Successfully');
      onAccountChange();
    } catch (error) {
      formatErrorMessage(error);
      setIsLoading(false);
    }
  };
  return (
    <>
      <div
        className={classNames({
          settlements__account: true,
          'settlements__account--default': settlement.default
        })}>
        <div className="settlements__account__icon">
          <Home />
        </div>
        <p className="settlements__account__details">
          {settlement && formatWallet(settlement.account_address)} ||{' '}
          {(settlement && settlement.account_provider) || formatCurrency(settlement.currency_id)}
        </p>
        <button className="settlements__account__button" onClick={() => setShowDropdown(true)}>
          <Ellipsis />
        </button>
        {showDropdown && (
          <AccountDropdown
            onClose={() => setShowDropdown(false)}
            setCurrentModal={setCurrentModal}
          />
        )}
      </div>
      {currentModal === 'delete' && (
        <WarningModal
          onModalClose={() => setCurrentModal('')}
          header={{ name: 'Delete', cursive: 'account' }}>
          <p className="modal__text">Are you sure you want to delete this account?</p>
          <Button variant="outline" isFull onClick={() => setCurrentModal('')}>
            Cancel
          </Button>
          <br />
          <Button isFull onClick={deleteAccount} isLoading={isLoading}>
            Yes, Continue
          </Button>
        </WarningModal>
      )}
      {currentModal === 'default' && (
        <WarningModal
          onModalClose={() => setCurrentModal('')}
          header={{ name: 'Change default', cursive: 'account' }}>
          <p className="modal__text">
            Do you want to make this account the default settlement account? <br />
            All your settlements would be made into this account.
          </p>
          <Button variant="outline" isFull onClick={() => setCurrentModal('')}>
            Cancel
          </Button>
          <br />
          <Button isFull onClick={setAsDefault} isLoading={isLoading}>
            Yes, Continue
          </Button>
        </WarningModal>
      )}
      {currentModal === 'success' && (
        <SuccessModal
          onModalClose={() => setCurrentModal('')}
          message="Your default settlement account has been changed successfully. Please note that it may take up to 24 hours for this change to be confirmed."
        />
      )}
    </>
  );
};

export { Account };

const AccountDropdown = ({ onClose, setCurrentModal }) => {
  const handleAction = (action) => {
    setCurrentModal(action);
    onClose();
  };
  return (
    <ClickAwayListener onClickAway={() => onClose()}>
      <div className="settlements__account__dropdown-menu">
        <button
          className="settlements__account__dropdown-menu-item"
          onClick={() => handleAction('default')}>
          <Settings />
          Set as default
        </button>
        <button
          className="settlements__account__dropdown-menu-item text-error"
          onClick={() => handleAction('delete')}>
          <Delete />
          Delete account
        </button>
      </div>
    </ClickAwayListener>
  );
};

Account.propTypes = {
  settlement: PropTypes.object,
  currencies: PropTypes.array,
  onAccountChange: PropTypes.func
};

AccountDropdown.propTypes = {
  onClose: PropTypes.func,
  setCurrentModal: PropTypes.func
};
